import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Search, X, Tag, Package, Car, Loader2, CornerDownLeft } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

/** Basic debounce hook */
function useDebounced(value, delay = 220) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

export default function GlobalSearchModal({ open, onClose }) {
  const { lang } = useLang();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const debounced = useDebounced(q, 220);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ brands: [], models: [], products: [] });
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults({ brands: [], models: [], products: [] });
      const id = setTimeout(() => inputRef.current?.focus(), 30);
      document.body.style.overflow = "hidden";
      return () => {
        clearTimeout(id);
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const query = debounced.trim();
    if (query.length < 2) {
      setResults({ brands: [], models: [], products: [] });
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    setLoading(true);
    api.get("/catalog/search", { params: { q: query, limit: 6 }, signal: ctl.signal })
      .then((r) => setResults(r.data || { brands: [], models: [], products: [] }))
      .catch((e) => { if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") setResults({ brands: [], models: [], products: [] }); })
      .finally(() => setLoading(false));
    return () => ctl.abort();
  }, [debounced, open]);

  const total = (results.brands?.length || 0) + (results.models?.length || 0) + (results.products?.length || 0);

  const goto = (path) => {
    // Restore body scroll SYNCHRONOUSLY before navigating. React 18's effect-cleanup
    // is scheduled after commit → on Android WebView / in-app browsers (FB/IG/WA)
    // the new page can appear blank because body.overflow="hidden" is still in effect
    // when the new route paints. Reset it immediately here to avoid the blank page.
    if (typeof document !== "undefined") document.body.style.overflow = "";
    onClose?.();
    nav(path);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
    if (e.key === "Enter") {
      // Fallback: go to /catalogo?search=q
      const query = q.trim();
      if (query.length >= 2) {
        e.preventDefault();
        goto(`/catalogo?search=${encodeURIComponent(query)}`);
      }
    }
  };

  const L = useMemo(() => (lang === "de" ? {
    placeholder: "Marke, Modell oder Produkt suchen (z. B. Golf 7, BMW, Fiat 500)…",
    brands: "Marken", models: "Modelle", products: "Produkte",
    empty_hint: "Tippe, um dein Auto und das passende Produkt zu finden.",
    no_results: "Keine Ergebnisse für", enter_hint: "um den gesamten Katalog zu durchsuchen",
    products_count: "Produkte",
  } : lang !== "it" ? {
    placeholder: "Search brand, model or product (e.g. Golf 7, BMW, Fiat 500)…",
    brands: "Brands", models: "Models", products: "Products",
    empty_hint: "Start typing to find your car and product.",
    no_results: "No results for", enter_hint: "to search all catalog",
    products_count: "products",
  } : {
    placeholder: "Cerca marca, modello o prodotto (es. Golf 7, BMW, Fiat 500)…",
    brands: "Marche", models: "Modelli", products: "Prodotti",
    empty_hint: "Inizia a digitare per trovare la tua auto e il prodotto.",
    no_results: "Nessun risultato per", enter_hint: "per cercare in tutto il catalogo",
    products_count: "prodotti",
  }), [lang]);

  if (!open) return null;

  return createPortal((
    <div className="fixed inset-0 z-[110]" data-testid="global-search-modal" onKeyDown={onKeyDown}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative mx-auto mt-[8vh] w-[min(92vw,680px)] bg-xd-dark border border-xd-cyan/25 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-xd-darkBorder">
          <Search className="w-5 h-5 text-xd-cyan flex-shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="global-search-input"
            placeholder={L.placeholder}
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            className="flex-1 bg-transparent border-none outline-none text-white text-base placeholder:text-slate-500"
          />
          {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
          <button
            type="button"
            onClick={onClose}
            data-testid="global-search-close"
            className="w-8 h-8 rounded-md text-slate-400 hover:text-white hover:bg-xd-dark2 flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto">
          {q.trim().length < 2 ? (
            <div className="px-6 py-8 text-center text-slate-400 text-sm">
              {L.empty_hint}
            </div>
          ) : total === 0 && !loading ? (
            <div className="px-6 py-8 text-center text-slate-400 text-sm" data-testid="global-search-empty">
              {L.no_results} <span className="text-white font-medium">&quot;{q}&quot;</span>
            </div>
          ) : (
            <div className="divide-y divide-xd-darkBorder">
              {results.brands?.length > 0 && (
                <SearchGroup icon={Tag} label={L.brands} testid="global-search-brands">
                  {results.brands.map((b) => (
                    <SearchRow
                      key={`b-${b.brand}`}
                      testid={`global-search-brand-${b.brand}`}
                      onClick={() => goto(`/catalogo?brand=${encodeURIComponent(b.brand)}`)}
                      leading={<Tag className="w-4 h-4 text-xd-cyan" />}
                      title={b.brand}
                      meta={`${b.count} ${L.products_count}`}
                    />
                  ))}
                </SearchGroup>
              )}
              {results.models?.length > 0 && (
                <SearchGroup icon={Car} label={L.models} testid="global-search-models">
                  {results.models.map((m) => (
                    <SearchRow
                      key={`m-${m.brand}-${m.model}`}
                      testid={`global-search-model-${m.brand}-${m.model}`}
                      onClick={() => goto(`/catalogo?brand=${encodeURIComponent(m.brand)}&model=${encodeURIComponent(m.model)}`)}
                      leading={<Car className="w-4 h-4 text-xd-orange" />}
                      title={<span><span className="text-slate-400">{m.brand}</span> · <span className="text-white">{m.model}</span></span>}
                      meta={`${m.count} ${L.products_count}`}
                    />
                  ))}
                </SearchGroup>
              )}
              {results.products?.length > 0 && (
                <SearchGroup icon={Package} label={L.products} testid="global-search-products">
                  {results.products.map((p) => (
                    <SearchRow
                      key={`p-${p.id}`}
                      testid={`global-search-product-${p.id}`}
                      onClick={() => goto(`/prodotto/${p.id}`)}
                      leading={
                        p.images?.[0] ? (
                          <img src={p.images[0]} alt="" className="w-10 h-10 rounded-md object-cover bg-xd-dark2" />
                        ) : (
                          <div className="w-10 h-10 rounded-md bg-xd-dark2 flex items-center justify-center"><Package className="w-4 h-4 text-slate-500" /></div>
                        )
                      }
                      title={<span className="line-clamp-1 text-white text-sm">{p.name}</span>}
                      meta={`€ ${p.price?.toFixed?.(0) ?? p.price ?? "—"}`}
                    />
                  ))}
                </SearchGroup>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-xd-darkBorder text-[11px] text-slate-500 font-mono-tech">
          <span className="inline-flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> {L.enter_hint}</span>
          <span>ESC</span>
        </div>
      </div>
    </div>
  ), document.body);
}

function SearchGroup({ icon: Icon, label, children, testid }) {
  return (
    <div className="py-2" data-testid={testid}>
      <div className="px-5 py-2 flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest text-slate-500">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <ul>{children}</ul>
    </div>
  );
}

function SearchRow({ leading, title, meta, onClick, testid }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-xd-dark2 text-left transition-colors"
      >
        <div className="flex-shrink-0">{leading}</div>
        <div className="flex-1 min-w-0 text-sm text-slate-200">{title}</div>
        <div className="flex-shrink-0 text-[11px] font-mono-tech text-slate-500">{meta}</div>
      </button>
    </li>
  );
}
